import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { fetchProgress, saveProgress } from "../lib/api";

const ProgressCtx = createContext(null);

const SESSION_KEY = "pc_session_id";

function getSessionId() {
  let sid = localStorage.getItem(SESSION_KEY);
  if (!sid) {
    sid = "sess-" + Math.random().toString(36).slice(2, 10) + Date.now().toString(36);
    localStorage.setItem(SESSION_KEY, sid);
  }
  return sid;
}

const DEFAULT_PROGRESS = {
  session_id: "",
  xp: 0,
  streak: 0,
  last_active: null,
  completed_lessons: [],
  badges: [],
  module_progress: {},
};

export function ProgressProvider({ children }) {
  const [sessionId] = useState(getSessionId);
  const [progress, setProgress] = useState(DEFAULT_PROGRESS);
  const [loading, setLoading] = useState(true);

  // Load once
  useEffect(() => {
    (async () => {
      try {
        const data = await fetchProgress(sessionId);
        setProgress({ ...DEFAULT_PROGRESS, ...data, session_id: sessionId });
      } catch (e) {
        console.warn("Failed to load progress, using local defaults", e);
        setProgress({ ...DEFAULT_PROGRESS, session_id: sessionId });
      } finally {
        setLoading(false);
      }
    })();
  }, [sessionId]);

  const persist = useCallback(async (next) => {
    setProgress(next);
    try { await saveProgress(next); } catch (e) { console.warn("save failed", e); }
  }, []);

  const markLessonComplete = useCallback((lessonId, xpGain = 20) => {
    setProgress((prev) => {
      if (prev.completed_lessons.includes(lessonId)) return prev;
      const today = new Date().toISOString().slice(0, 10);
      const yesterday = new Date(Date.now() - 86400000).toISOString().slice(0, 10);
      let streak = prev.streak || 0;
      if (prev.last_active === today) {
        // same day, keep streak
      } else if (prev.last_active === yesterday) {
        streak += 1;
      } else {
        streak = 1;
      }
      const next = {
        ...prev,
        xp: prev.xp + xpGain,
        streak,
        last_active: today,
        completed_lessons: [...prev.completed_lessons, lessonId],
      };
      if (!next.badges.includes("first-steps")) {
        next.badges = [...next.badges, "first-steps"];
      }
      saveProgress(next).catch(() => {});
      return next;
    });
  }, []);

  const awardBadge = useCallback((badgeId) => {
    setProgress((prev) => {
      if (prev.badges.includes(badgeId)) return prev;
      const next = { ...prev, badges: [...prev.badges, badgeId] };
      saveProgress(next).catch(() => {});
      return next;
    });
  }, []);

  const setModuleStep = useCallback((moduleId, stepIndex) => {
    setProgress((prev) => {
      const mp = { ...(prev.module_progress || {}) };
      mp[moduleId] = Math.max(mp[moduleId] || 0, stepIndex);
      const next = { ...prev, module_progress: mp };
      saveProgress(next).catch(() => {});
      return next;
    });
  }, []);

  const resetAll = useCallback(() => {
    const fresh = { ...DEFAULT_PROGRESS, session_id: sessionId };
    persist(fresh);
  }, [sessionId, persist]);

  return (
    <ProgressCtx.Provider
      value={{ progress, loading, sessionId, markLessonComplete, awardBadge, setModuleStep, resetAll }}
    >
      {children}
    </ProgressCtx.Provider>
  );
}

export function useProgress() {
  const ctx = useContext(ProgressCtx);
  if (!ctx) throw new Error("useProgress must be used within ProgressProvider");
  return ctx;
}
